import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { useSubtitleSettings } from "../../../context/SubtitleSettingsContext";
import { useVideo } from "../../../context/VideoContext";
import "./SubtitleStyle.css";
import { getPortalContainer } from "../../../utils/portalContainer";

const SubtitleStyle = ({ onClose }) => {
  const { settings, updateSubtitleSetting } = useSubtitleSettings();
  const { isFullscreen, isIOS } = useVideo();
  const [activeSubtitle, setActiveSubtitle] = useState(1); // 1 یا 2
  const [previewText, setPreviewText] = useState("This is a sample subtitle");

  // ✨ تشخیص موبایل
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 480);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // وقتی زیرنویس فارسی انتخاب شده، متن پیش‌نمایش رو تغییر بده
  useEffect(() => {
    if (activeSubtitle === 2 && settings.subtitle2.language === "fa") {
      setPreviewText("این یک نمونه زیرنویس است");
    } else {
      setPreviewText("This is a sample subtitle");
    }
  }, [
    activeSubtitle,
    settings.subtitle1.language,
    settings.subtitle2.language,
  ]);

  const currentSettings = settings[`subtitle${activeSubtitle}`];

  // استخراج opacity از bgColor
  const getOpacity = (bgColor) => {
    const match = bgColor.match(/[\d\.]+\)$/);
    if (match) {
      return parseFloat(match[0].replace(")", ""));
    }
    return 0.85;
  };

  // تبدیل rgba به hex برای color picker
  const rgbaToHex = (bgColor) => {
    const match = bgColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (match) {
      const r = parseInt(match[1]).toString(16).padStart(2, "0");
      const g = parseInt(match[2]).toString(16).padStart(2, "0");
      const b = parseInt(match[3]).toString(16).padStart(2, "0");
      return `#${r}${g}${b}`;
    }
    return "#000000";
  };

  return ReactDOM.createPortal(
    <div
      className="subtitle-style-overlay"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <div
        className={`subtitle-style-panel ${isMobile ? "ss-mobile" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="subtitle-style-header">
          <h3>🎨 استایل زیرنویس</h3>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="subtitle-style-content">
          {/* انتخاب زیرنویس */}
          <div className="subtitle-tabs">
            <button
              className={`subtitle-tab ${activeSubtitle === 1 ? "active" : ""}`}
              onClick={() => setActiveSubtitle(1)}
            >
              {isMobile ? "زیر ۱" : "زیرنویس یک"}
            </button>
            <button
              className={`subtitle-tab ${activeSubtitle === 2 ? "active" : ""}`}
              onClick={() => setActiveSubtitle(2)}
            >
              {isMobile ? "زیر ۲" : "زیرنویس دو"}
            </button>
          </div>

          {/* پیش‌نمایش */}
          <div
            className="style-preview"
            style={{
              "--preview-font-size": `${currentSettings.fontSize}px`,
              "--preview-color": currentSettings.color,
              "--preview-bg": currentSettings.bgColor,
              "--preview-font-family":
                activeSubtitle === 1 ? "Roboto" : "Vazirmatn",
              "--preview-direction": activeSubtitle === 1 ? "ltr" : "rtl",
            }}
          >
            <div className="preview-box">{previewText}</div>
          </div>

          {/* کنترل‌ها */}
          <div className="style-controls">
            {/* اندازه فونت */}
            <div className="control-group">
              <label>
                اندازه فونت:{" "}
                <span className="control-value">
                  {currentSettings.fontSize}px
                </span>
              </label>
              <div className="range-control">
                <input
                  type="range"
                  min="8"
                  max="35"
                  step="1"
                  value={currentSettings.fontSize}
                  onChange={(e) =>
                    updateSubtitleSetting(
                      activeSubtitle,
                      "fontSize",
                      parseInt(e.target.value),
                    )
                  }
                />
                {!isMobile && (
                  <div className="range-values">
                    <span>۸</span>
                    <span>۲۰</span>
                    <span>۳۵</span>
                  </div>
                )}
              </div>
            </div>

            {/* رنگ متن */}
            <div className="control-group">
              <label>رنگ متن</label>
              <div className="color-control">
                <input
                  type="color"
                  value={currentSettings.color}
                  onChange={(e) =>
                    updateSubtitleSetting(
                      activeSubtitle,
                      "color",
                      e.target.value,
                    )
                  }
                  className="color-picker"
                />
                <input
                  type="text"
                  value={currentSettings.color}
                  onChange={(e) =>
                    updateSubtitleSetting(
                      activeSubtitle,
                      "color",
                      e.target.value,
                    )
                  }
                  className="color-input"
                  placeholder="#ffffff"
                />
              </div>
            </div>

            {/* رنگ پس‌زمینه */}
            <div className="control-group">
              <label>رنگ پس‌زمینه</label>
              <div className="color-control">
                <input
                  type="color"
                  value={rgbaToHex(currentSettings.bgColor)}
                  onChange={(e) => {
                    const color = e.target.value;
                    const r = parseInt(color.slice(1, 3), 16);
                    const g = parseInt(color.slice(3, 5), 16);
                    const b = parseInt(color.slice(5, 7), 16);
                    const opacity = getOpacity(currentSettings.bgColor);
                    updateSubtitleSetting(
                      activeSubtitle,
                      "bgColor",
                      `rgba(${r}, ${g}, ${b}, ${opacity})`,
                    );
                  }}
                  className="color-picker"
                />
                {!isMobile && (
                  <input
                    type="text"
                    value={currentSettings.bgColor}
                    onChange={(e) =>
                      updateSubtitleSetting(
                        activeSubtitle,
                        "bgColor",
                        e.target.value,
                      )
                    }
                    className="color-input"
                    placeholder="rgba(0,0,0,0.85)"
                  />
                )}
              </div>
            </div>

            {/* opacity پس‌زمینه */}
            <div className="control-group">
              <label>
                شفافیت پس‌زمینه:{" "}
                <span className="control-value">
                  {Math.round(getOpacity(currentSettings.bgColor) * 100)}%
                </span>
              </label>
              <div className="range-control">
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={getOpacity(currentSettings.bgColor)}
                  onChange={(e) => {
                    const opacity = e.target.value;
                    const rgba = currentSettings.bgColor.replace(
                      /[\d\.]+\)$/,
                      `${opacity})`,
                    );
                    updateSubtitleSetting(activeSubtitle, "bgColor", rgba);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="subtitle-style-footer">
          <button className="done-button" onClick={onClose}>
            {isMobile ? "✓ تایید" : "تایید"}
          </button>
        </div>
      </div>
    </div>,
    getPortalContainer(isIOS, isFullscreen),
  );
};

export default SubtitleStyle;
